import { Button, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ReactElement, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportClickSummaryCreate } from '../../../../DataProvider';
import type { ClicksSummariesResource } from '../../../../Entities/ClicksSummary';
import { getClicksSummaryReportParams } from './utils';
import type { ReportFilter, ReportGranularity } from '../../../../Components/ReportsWidget/utils';

export interface PreviewButtonProps {
  onPreviewStart: (granularity: ReportGranularity) => void;
  onPreviewSuccess: (records: ClicksSummariesResource[]) => void;
  onPreviewError: () => void;
  autoPreviewOnMount?: boolean;
}

export default function PreviewButton({
  onPreviewStart,
  onPreviewSuccess,
  onPreviewError,
  autoPreviewOnMount = false,
}: PreviewButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportClickSummaryCreate();
  const hasAutoPreviewTriggered = useRef(false);

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const clicksSummariesListAccess = useCanAccess({
    resource: 'clicks_summaries',
    action: 'list',
  });

  const handlePreview = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    const filter = values as ReportFilter;
    onPreviewStart(filter.granularity === 'hourly' ? 'hourly' : 'daily');

    try {
      const result = await runReport(getClicksSummaryReportParams(filter));
      onPreviewSuccess(result.data);
    } catch (error) {
      onPreviewError();
      notify(error instanceof Error ? error.message : 'Preview failed', { type: 'error' });
    }
  });

  useEffect(() => {
    if (
      !autoPreviewOnMount ||
      hasAutoPreviewTriggered.current ||
      !reportsCreateAccess.canAccess ||
      !clicksSummariesListAccess.canAccess
    ) {
      return;
    }

    hasAutoPreviewTriggered.current = true;
    void handlePreview();
  }, [
    autoPreviewOnMount,
    clicksSummariesListAccess.canAccess,
    handlePreview,
    reportsCreateAccess.canAccess,
  ]);

  if (!reportsCreateAccess.canAccess || !clicksSummariesListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      onClick={() => void handlePreview()}
      disabled={report.isPending}
      startIcon={
        report.isPending ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />
      }
    >
      {report.isPending ? 'Loading...' : 'Preview'}
    </Button>
  );
}
